import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import {
  completeVenueOnboarding,
  validateVenueOnboardingInvite
} from '../../services/venueOnboardingApi';
import { usePageSeo } from '../../components/Seo/PageSeo';
import './VenueOnboardingPage.css';

const VENUE_TYPES = [
  { value: 'pub', label: 'Pub / bar' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Café' },
  { value: 'event_space', label: 'Event space' },
  { value: 'studio', label: 'Studio' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'conference_room', label: 'Conference room' },
  { value: 'outdoor_space', label: 'Outdoor space' },
  { value: 'loft', label: 'Loft' },
  { value: 'banquet_hall', label: 'Banquet hall' },
  { value: 'auditorium', label: 'Auditorium' }
];

const emptyForm = {
  invite_code: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone: '',
  venue_name: '',
  address: '',
  city: '',
  postal_code: '',
  venue_type: 'pub',
  max_capacity: '',
  description: '',
  space_name: 'Main space'
};

const VenueOnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const inviteFromUrl = searchParams.get('invite') || '';
  const [form, setForm] = useState({ ...emptyForm, invite_code: inviteFromUrl });
  const [inviteStatus, setInviteStatus] = useState({ loading: !!inviteFromUrl, valid: null, message: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const validateInvite = useCallback(async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) {
      setInviteStatus({ loading: false, valid: null, message: '', note: '' });
      return;
    }
    setInviteStatus((s) => ({ ...s, loading: true }));
    try {
      const result = await validateVenueOnboardingInvite(trimmed);
      setInviteStatus({
        loading: false,
        valid: result.valid,
        message: result.message || '',
        note: result.note || ''
      });
    } catch (e) {
      setInviteStatus({
        loading: false,
        valid: false,
        message: e.message || 'Could not validate invite',
        note: ''
      });
    }
  }, []);

  useEffect(() => {
    if (inviteFromUrl) {
      setForm((f) => ({ ...f, invite_code: inviteFromUrl }));
      validateInvite(inviteFromUrl);
    }
  }, [inviteFromUrl, validateInvite]);

  usePageSeo({
    title: 'Venue onboarding',
    description:
      'Create your Occasia venue account with an invite code. Set up your listing, main space, and dashboard access in minutes.',
    path: '/venue-onboarding'
  });

  const canSubmit = useMemo(() => {
    if (!inviteStatus.valid) return false;
    const cap = Number(form.max_capacity);
    return (
      form.invite_code.trim() &&
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.email.trim() &&
      form.password.length >= 8 &&
      form.venue_name.trim() &&
      form.address.trim() &&
      form.city.trim() &&
      form.postal_code.trim() &&
      Number.isFinite(cap) &&
      cap >= 1
    );
  }, [form, inviteStatus.valid]);

  const onChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (field === 'invite_code') {
      validateInvite(e.target.value);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await completeVenueOnboarding({
        invite_code: form.invite_code.trim(),
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        venue_name: form.venue_name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        venue_type: form.venue_type,
        max_capacity: Number(form.max_capacity),
        description: form.description.trim() || null,
        space_name: form.space_name.trim() || 'Main space'
      });
      setDone(result);
      if (result.access_token) {
        localStorage.setItem('access_token', result.access_token);
        if (result.user) {
          localStorage.setItem('user', JSON.stringify(result.user));
        }
      }
      const dash = (result.dashboard_url || 'https://venues.occasia.io').replace(/\/$/, '');
      const redirect = `${dash}/dashboard-login?access_token=${encodeURIComponent(result.access_token)}&venueId=${encodeURIComponent(result.venue_id)}`;
      window.setTimeout(() => {
        window.location.href = redirect;
      }, 1800);
    } catch (err) {
      setError(err.message || 'Could not complete onboarding');
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageWrapper useNewFooter useLanding2Header>
        <main className="venue-onboard-page">
          <div className="venue-onboard-card venue-onboard-card--success">
            <h1>You&apos;re in</h1>
            <p>{done.message}</p>
            <p className="venue-onboard-muted">Redirecting to your venue dashboard…</p>
            <a className="venue-onboard-btn" href={`${done.dashboard_url}/dashboard-login?venueId=${done.venue_id}`}>
              Open dashboard now
            </a>
          </div>
        </main>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="venue-onboard-page">
        <div className="venue-onboard-card">
          <p className="venue-onboard-eyebrow">Venue partners</p>
          <h1>Create your Occasia listing</h1>
          <p className="venue-onboard-lead">
            You need an invite code from Occasia. Required fields are marked. Everything else can be
            completed in the dashboard.
          </p>

          <form className="venue-onboard-form" onSubmit={onSubmit}>
            <fieldset className="venue-onboard-fieldset">
              <legend>Invite</legend>
              <label>
                Invite code <span className="req">*</span>
                <input
                  name="invite_code"
                  value={form.invite_code}
                  onChange={onChange('invite_code')}
                  placeholder="OCC-XXXXXXXX"
                  autoComplete="off"
                  required
                />
              </label>
              {inviteStatus.loading ? <p className="venue-onboard-hint">Checking invite…</p> : null}
              {!inviteStatus.loading && inviteStatus.valid === false ? (
                <p className="venue-onboard-error">{inviteStatus.message}</p>
              ) : null}
              {!inviteStatus.loading && inviteStatus.valid ? (
                <p className="venue-onboard-ok">Invite accepted{inviteStatus.note ? `: ${inviteStatus.note}` : ''}</p>
              ) : null}
            </fieldset>

            <fieldset className="venue-onboard-fieldset">
              <legend>Your account</legend>
              <div className="venue-onboard-row">
                <label>
                  First name <span className="req">*</span>
                  <input value={form.first_name} onChange={onChange('first_name')} required />
                </label>
                <label>
                  Last name <span className="req">*</span>
                  <input value={form.last_name} onChange={onChange('last_name')} required />
                </label>
              </div>
              <label>
                Work email <span className="req">*</span>
                <input type="email" value={form.email} onChange={onChange('email')} required />
              </label>
              <label>
                Password <span className="req">*</span>
                <input type="password" value={form.password} onChange={onChange('password')} minLength={8} required />
              </label>
              <label>
                Phone <span className="optional">(optional)</span>
                <input type="tel" value={form.phone} onChange={onChange('phone')} />
              </label>
            </fieldset>

            <fieldset className="venue-onboard-fieldset">
              <legend>Venue basics</legend>
              <label>
                Venue name <span className="req">*</span>
                <input value={form.venue_name} onChange={onChange('venue_name')} required />
              </label>
              <label>
                Street address <span className="req">*</span>
                <input value={form.address} onChange={onChange('address')} required />
              </label>
              <div className="venue-onboard-row">
                <label>
                  City <span className="req">*</span>
                  <input value={form.city} onChange={onChange('city')} required />
                </label>
                <label>
                  Postcode <span className="req">*</span>
                  <input value={form.postal_code} onChange={onChange('postal_code')} required />
                </label>
              </div>
              <div className="venue-onboard-row">
                <label>
                  Venue type <span className="req">*</span>
                  <select value={form.venue_type} onChange={onChange('venue_type')}>
                    {VENUE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Seated capacity <span className="req">*</span>
                  <input
                    type="number"
                    min={1}
                    value={form.max_capacity}
                    onChange={onChange('max_capacity')}
                    required
                  />
                </label>
              </div>
              <label>
                Main space name <span className="optional">(optional)</span>
                <input value={form.space_name} onChange={onChange('space_name')} />
              </label>
              <label>
                Short description <span className="optional">(optional — add more in dashboard)</span>
                <textarea rows={4} value={form.description} onChange={onChange('description')} />
              </label>
            </fieldset>

            {error ? <p className="venue-onboard-error">{error}</p> : null}

            <button type="submit" className="venue-onboard-btn" disabled={!canSubmit || submitting}>
              {submitting ? 'Creating…' : 'Create venue & open dashboard'}
            </button>
          </form>

          <p className="venue-onboard-footer">
            Already have an account?{' '}
            <Link to="/dashboard-login">Sign in to the venue dashboard</Link>
          </p>
        </div>
      </main>
    </PageWrapper>
  );
};

export default VenueOnboardingPage;
